import React from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, CssBaseline, IconButton, Avatar, Menu, MenuItem, Tooltip, useMediaQuery, Divider, Button, Alert, Snackbar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import apiClient from '../services/api';
import { getApplicationInitData } from '../services/applicationService';
import { Stepper, Step, StepLabel, Tabs, Tab } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EditIcon from '@mui/icons-material/Edit';
import { TextField, Grid, FormControl, InputLabel, Select, FormHelperText } from '@mui/material';
import {
  PersonalInfoForm,
  EducationForm,
  WorkExperienceForm,
  EmergencyContactForm,
  RefereeForm,
  AdditionalInfoForm,
  MaterialsForm
} from '../components/dashboard';

// TODO: 替换为你的实际API域名
const REACT_ATTACHMENT_URL = process.env.REACT_APP_ATTACHMENT_URL || 'http://localhost:8080/';

const drawerWidth = 240;

const menuItems = [
  { text: '我的申请', path: '/dashboard', icon: <SchoolIcon /> },
  { text: '我的账户', path: '#', icon: <PeopleIcon /> },
];

const applicationTabs = [
  { label: '个人信息', key: 'profile' },
  { label: '教育背景', key: 'education' },
  { label: '工作经历', key: 'work' },
  { label: '紧急联系人', key: 'contact' },
  { label: '推荐人', key: 'referee' },
  { label: '其他信息', key: 'additional' },
  { label: '专业与院校', key: 'program' },
  { label: '申请材料', key: 'materials' },
  { label: '服务套餐', key: 'sevice' },
  { label: '支付信息', key: 'payment' }
];

// 进度条数据提升到全局，供ApplicationPage和Dashboard共用
const progressData = [
  { en: 'Submitted', cn: '提交申请' },
  { en: 'Review', cn: '申请审核' },
  // { en: 'Assigned', cn: '分配顾问' },
  { en: 'Pre-Review', cn: '资料审核' },
  { en: 'Application-Submit', cn: '递交学校' },
  { en: 'School-Review', cn: '学校审核' },
  { en: 'Offer', cn: '取得offer' },
  { en: 'EMGS', cn: '签证' },
  { en: 'Entry', cn: '入境' },
  { en: 'Register', cn: '入学' },
  { en: 'Completed', cn: '结案' }
];

// 新增：其他信息字段值映射
const additionalInfoValueMap = {
  accommodationNeeds: {
    dormitory: '学校宿舍',
    homestay: '寄宿家庭',
    apartment: '独立公寓',
    no_need: '不需要',
  },
  studyBudget: {
    '50k': '5万以下',
    '50k-100k': '5-10万',
    '100k-200k': '10-20万',
    '200k': '20万以上',
  },
  visaHistory: {
    none: '无',
    approved: '有，已获签',
    rejected: '有，被拒签',
  },
};
// 新增：其他信息值映射函数
const getAdditionalInfoDisplayValue = (key, value) => {
  if (!value) return '—';
  if (additionalInfoValueMap[key] && value in additionalInfoValueMap[key]) {
    return additionalInfoValueMap[key][value];
  }
  return value;
};
// 新增：其他信息字段label
const additionalFieldLabels = {
  preferredStartDate: '期望入学时间',
  studyBudget: '学习预算',
  accommodationNeeds: '住宿需求',
  visaHistory: '签证记录',
  additionalInfo: '补充信息',
  workExperience: '工作经历说明',
  motivation: '学习动机',
  careerGoals: '职业目标',
};

function ApplicationProfile({ personalInfo }) {
  // 字段映射
  const fieldLabels = {
    fullName: '姓名',
    gender: '性别',
    dateOfBirth: '出生日期',
    nationality: '国籍',
    passportNumber: '护照号码',
    idCardNumber: '身份证号',
    maritalStatus: '婚姻状况',
    religion: '宗教信仰',
    passportIssueDate: '护照签发日期',
    passportExpiryDate: '护照到期日期',
    passportIssuePlace: '护照签发地',
    phone: '手机号码',
    email: '电子邮箱',
    bodyHeight: '身高（CM）',
    bodyWeight: '体重（KG）',
    salary: '月薪',
    siblingsCount: '兄弟姐妹数',
    siblingsRanking: '在兄弟姐妹排行',
    ifCee: '是否参加高考',
    motherName: '母亲姓名',
    fatherName: '父亲姓名',
    fatherSalary: '父亲月薪',
    CorrespondenceAddress: '通信地址',
    PermanentAddress: '永久地址'
  };

  // 值映射函数
  const getDisplayValue = (key, value) => {
    if (!value) return '—';
    
    switch (key) {
      case 'gender':
        return value === 'Male' ? '男' : value === 'Female' ? '女' : value;
      case 'maritalStatus':
        switch (value) {
          case 'Single': return '单身';
          case 'Married': return '已婚';
          case 'Widower': return '鳏夫';
          case 'Widow': return '寡妇';
          default: return value;
        }
      case 'religion':
        switch (value) {
          case 'NO RELIGION': return '无宗教信仰';
          case 'TAO': return '道教';
          case 'CONFUCIANISM': return '儒教';
          case 'BUDDHIST': return '佛教';
          case 'BAHAI': return '巴哈伊教';
          case 'JUDAISM': return '犹太教';
          case 'HINDU': return '印度教';
          case 'ISLAM': return '伊斯兰教';
          case 'CHRISTIAN': return '基督教';
          case 'SIKHISM': return '锡克教';
          case 'OTHERS': return '其他';
          default: return value;
        }
      case 'ifCee':
        return value === '1' ? '是' : value === '0' ? '否' : value;
      default:
        return value;
    }
  };

  if (!personalInfo) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无个人信息</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {Object.entries(personalInfo).map(([key, value]) => (
          <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
            <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{getDisplayValue(key, value)}</Box>
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function ApplicationEnglish({ englishLanguage }) {
  const fieldLabels = {
    languageType: '语言考试类型',
    languageCustomType: '其他语言类型',
    languageResult: '语言成绩',
  };

  // 语言类型映射
  const languageTypeMap = {
    IELTS: '雅思',
    TOEFL: '托福',
    'Cambridge English Qualifications': '剑桥英语',
    PTE: 'PTE',
    Linguaskill: '领思',
    MUET: 'MUET',
    HEET: 'HEET',
    'Duolingo English Test': '多邻国',
    NOT: '暂未取得',
    OTHERS: '其他',
  };

  if (!englishLanguage) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无英语语言信息</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: 16 }}>英语语言信息</Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {/* 语言类型特殊处理 */}
        <Typography sx={{ mb: 0.5, fontSize: 14 }}>
          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels.languageType}：</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {englishLanguage.languageType === 'OTHERS'
              ? (englishLanguage.languageCustomType ? `其他（${englishLanguage.languageCustomType}）` : '其他')
              : (languageTypeMap[englishLanguage.languageType] || englishLanguage.languageType)}
          </Box>
        </Typography>
        {/* 语言成绩 */}
        <Typography sx={{ mb: 0.5, fontSize: 14 }}>
          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels.languageResult}：</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>{englishLanguage.languageResult || '—'}</Box>
        </Typography>
      </Box>
    </Box>
  );
}

function ApplicationEducation({ education }) {
  const fieldLabels = {
    currentEducationLevel: '当前教育程度',
    schoolName: '学校名称',
    major: '专业',
    gpa: '绩点',
    enrolledDate: '入学时间',
    graduationDate: '毕业时间',
    studyMode: '学习模式'
  };

  // 值映射函数
  const getDisplayValue = (key, value) => {
    if (!value) return '—';
    
    switch (key) {
      case 'currentEducationLevel':
        switch (value) {
          case 'High School': return '普通高中';
          case 'Technical secondary school': return '职高（中专）';
          case 'International High School': return '国际高中';
          case 'Diploma': return '大专';
          case 'Bachelor': return '本科';
          case 'Master': return '硕士';
          default: return value;
        }
      case 'studyMode':
        switch (value) {
          case 'Full-time': return '全日制';
          case 'Part-time': return '非全日制';
          default: return value;
        }
      default:
        return value;
    }
  };

  if (!Array.isArray(education) || education.length === 0) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无教育背景信息</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: 16 }}>教育背景</Typography> */}
      {education.map((item, idx) => (
        item ? (
          <Box key={idx} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              教育经历 {idx + 1}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{getDisplayValue(key, value)}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      ))}
    </Box>
  );
}

function ApplicationWork({ workExperience }) {
  const fieldLabels = {
    companyName: '公司名称',
    position: '职位',
    startDate: '开始日期',
    endDate: '结束日期',
    address: '地址',
    postalCode: '邮政编码',
    phone: '联系电话',
    workType: '工作类型',
    description: '工作内容',
    // 新增常见联系方式字段
    mobilePhone: '手机号码',
    mobile: '手机',
    workPhone: '工作电话',
    fax: '传真',
    email: '邮箱',
  };

  // 值映射函数
  const getDisplayValue = (key, value, item) => {
    switch (key) {
      case 'workType':
        if (!value) return '—';
        switch (value) {
          case 'gov': return '政府机/法定机构';
          case 'self': return '自雇人士';
          case 'private': return '私人单位';
          default: return value;
        }
      case 'endDate':
        // 如果至今仍在职，显示"至今"，否则显示具体结束日期
        const isCurrent = item?.isCurrent === true || item?.isCurrent === 'true';
        return isCurrent ? '至今' : (value || '—');
      default:
        return value || '—';
    }
  };

  if (!Array.isArray(workExperience) || workExperience.length === 0) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无工作经历</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: 16 }}>工作经历</Typography> */}
      {workExperience.map((item, idx) => (
        item ? (
          <Box
            key={idx}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              工作经历 {idx + 1}
            </Typography>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {Object.entries(item)
                .filter(([key]) => key in fieldLabels && key !== 'isCurrent') // 排除 isCurrent 字段，因为已经合并到 endDate 显示中
                .map(([key, value]) => (
                  <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                    <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key]}：</Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{getDisplayValue(key, value, item)}</Box>
                  </Typography>
                ))}
            </Box>
          </Box>
        ) : null
      ))}
    </Box>
  );
}

function ApplicationContact({ emergencyContact }) {
  const fieldLabels = {
    name: '姓名',
    birthDate: '出生日期',
    relation: '与申请人关系',
    companyName: '工作单位名称',
    position: '职位',
    address: '联系地址',
    postalCode: '邮政编码',
    homePhone: '家庭电话',
    companyPhone: '单位电话',
    mobilePhone: '手机号码',
    salary: '月薪(人民币)',
    idNumber: '身份证号',
    passportNumber: '护照号',
    // 添加可能缺失的字段映射
    email: '邮箱',
    phone: '电话',
    workPhone: '工作电话',
    homeAddress: '家庭地址',
    workAddress: '工作地址',
  };
  if (!Array.isArray(emergencyContact) || emergencyContact.length === 0) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无紧急联系人信息</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: 16 }}>紧急联系人</Typography> */}
      {emergencyContact.map((item, idx) => (
        item ? (
          <Box
            key={idx}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              紧急联系人 {idx + 1}
            </Typography>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{value || '—'}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      ))}
    </Box>
  );
}

function ApplicationReferee({ referee }) {
  const fieldLabels = {
    name: '姓名',
    phone: '手机号码',
    email: '邮箱',
    companyName: '工作单位名称',
    position: '职位',
    companyAddress: '公司地址',
    postalCode: '邮政编码',
    // 添加可能缺失的字段映射
    address: '地址',
    workPhone: '工作电话',
    mobile: '手机',
    fax: '传真',
    website: '网站',
  };
  if (!Array.isArray(referee) || referee.length === 0) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary" sx={{ fontSize: 14 }}>暂无推荐人信息</Typography></Box>;
  }
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: 16 }}>推荐人</Typography> */}
      {referee.map((item, idx) => (
        item ? (
          <Box
            key={idx}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'primary.main', fontSize: 14 }}>
              推荐人 {idx + 1}
            </Typography>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {Object.entries(item).map(([key, value]) => (
                <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{fieldLabels[key] || key}：</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>{value || '—'}</Box>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null
      ))}
    </Box>
  );
}

function ApplicationAdditionalInformation({ appData }) {
  return (
    <Box sx={{ width: '100%' }}>
      <ApplicationProfile personalInfo={appData.personalInfo} />
      <Divider sx={{ my: 2 }} />
      <ApplicationEnglish englishLanguage={appData.englishLanguage} />
      <Divider sx={{ my: 2 }} />
      <ApplicationEducation education={appData.education} />
      <Divider sx={{ my: 2 }} />
      <ApplicationWork workExperience={appData.workExperience} />
      <Divider sx={{ my: 2 }} />
      <ApplicationContact emergencyContact={appData.emergencyContact} />
      <Divider sx={{ my: 2 }} />
      <ApplicationReferee referee={appData.referee} />
      {/* 新增：其他信息展示 */}
      {appData.additionalInformation && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: 16 }}>其他信息</Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {Object.entries(appData.additionalInformation).map(([key, value]) => (
              <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{additionalFieldLabels[key] || key}：</Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>{getAdditionalInfoDisplayValue(key, value)}</Box>
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function ApplicationPage({ appData, loading, error, cardRadius, onEditClick, status }) {
  const [tab, setTab] = React.useState('profile');
  // 兼容后端数据结构，假设返回 { step1: [...], step2: [...], ... }
  // const data = appData[tab] || []; // 已不再使用，可删除

  // 处理材料数据为数组，带类型
  const materialsList = React.useMemo(() => {
    if (!appData.materials || typeof appData.materials !== 'object') {
      return [];
    }
    const result = Object.entries(appData.materials).map(([type, file]) => {
      // 如果file是对象（包含name和path），直接使用
      if (file && typeof file === 'object' && file.name && file.path) {
        return {
          ...file,
          type,
        };
      }
      // 如果file是数组，处理数组中的每个文件
      if (Array.isArray(file)) {
        return file.map(f => ({
          ...f,
          type,
        }));
      }
      return null;
    }).filter(Boolean).flat();
    return result;
  }, [appData.materials]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, overflowX: 'auto' }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: { xs: 48, sm: 40 },
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 40 },
              borderRadius: 0,
              mx: { xs: 0.25, sm: 0.5 },
              px: { xs: 1, sm: 2, md: 3 },
              fontWeight: 500,
              textTransform: 'none',
              bgcolor: 'transparent',
              color: 'text.primary',
              transition: 'none',
              fontSize: { xs: 12, sm: 14 },
              whiteSpace: 'nowrap',
            },
            '& .Mui-selected': {
              color: 'primary.main',
              bgcolor: 'transparent',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 2,
              bgcolor: 'primary.main',
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': { opacity: 0.3 },
            },
          }}
        >
          {applicationTabs.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ 
        bgcolor: 'background.paper', 
        borderRadius: cardRadius || 2, 
        boxShadow: 1, 
        p: { xs: 2, sm: 3 }, 
        minHeight: 200,
        overflowX: 'auto'
      }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>加载中...</Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', color: 'error.main', py: 6 }}>{error}</Box>
        ) : (
          <>
            {tab === 'profile' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>个人信息</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('profile')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <ApplicationProfile personalInfo={appData.personalInfo} />
                  <Divider sx={{ my: 2 }} />
                  <ApplicationEnglish englishLanguage={appData.englishLanguage} />
                </Box>
              </>
            )}
            {tab === 'education' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>教育背景</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('education')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <ApplicationEducation education={appData.education} />
              </>
            )}
            {tab === 'work' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>工作经历</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('work')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <ApplicationWork workExperience={appData.workExperience} />
              </>
            )}
            {tab === 'contact' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>紧急联系人</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('contact')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <ApplicationContact emergencyContact={appData.emergencyContact} />
              </>
            )}
            {tab === 'referee' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>推荐人</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('referee')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <ApplicationReferee referee={appData.referee} />
              </>
            )}
            {tab === 'additional' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>其他信息</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('additional')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                {appData.additionalInformation ? (
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        {Object.entries(appData.additionalInformation).map(([key, value]) => (
                          <Typography key={key} sx={{ mb: 0.5, fontSize: 14 }}>
                            <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{additionalFieldLabels[key] || key}：</Box>
                            <Box component="span" sx={{ color: 'text.secondary' }}>{getAdditionalInfoDisplayValue(key, value)}</Box>
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>暂无其他信息</Box>
                )}
              </>
            )}
            {tab === 'sevice' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>服务套餐</Typography> */}
                  {/* {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('sevice')}
                    >
                      修改
                    </Button>
                  )} */}
                </Box>
                {appData.selectedPackage ? (
                  <Box
                    sx={{
                      p: { xs: 2, sm: 3 },
                      borderRadius: cardRadius || 2,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, color: appData.selectedPackage.color || 'primary.main', mb: 1, fontSize: { xs: 18, sm: 20, md: 24 } }}>
                      {appData.selectedPackage.name}
                    </Typography>
                    <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: { xs: 14, sm: 16 } }}>
                      {appData.selectedPackage.description}
                    </Typography>
                    {appData.selectedPackage.price && (
                      <Typography sx={{ mb: 1, fontSize: { xs: 14, sm: 16 } }}>
                        价格：￥ <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{appData.selectedPackage.price}</Box>
                      </Typography>
                    )}
                    {Array.isArray(appData.selectedPackage.features) && appData.selectedPackage.features.length > 0 && (
                      <>
                        <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: { xs: 14, sm: 16 } }}>套餐包含：</Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {appData.selectedPackage.features.map((f, idx) => (
                            <li key={idx} style={{ marginBottom: 4, color: '#1976d2', fontSize: 14 }}>{f}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>暂无服务套餐信息</Box>
                )}
              </>
            )}
            {tab === 'materials' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>申请材料</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('materials')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  {materialsList.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: { xs: 8, sm: 12 }, textAlign: 'left', fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}>材料名称</th>
                          <th style={{ padding: { xs: 8, sm: 12 }, textAlign: 'left', fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}>文件名</th>
                          <th style={{ padding: { xs: 8, sm: 12 }, textAlign: 'left', fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialsList.map((file, idx) => {
                          return (
                            <tr key={file.path || idx} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: { xs: 8, sm: 12 }, fontSize: { xs: 12, sm: 14 } }}>{file.MaterialName}</td>
                              <td style={{ padding: { xs: 8, sm: 12 }, fontSize: { xs: 12, sm: 14 } }}>{file.name || '未命名'}</td>
                              <td style={{ padding: { xs: 8, sm: 12 }, fontSize: { xs: 12, sm: 14 } }}>
                                {file.path ? (
                                  <a href={REACT_ATTACHMENT_URL+ '/storage' + file.path} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>下载/预览</a>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>暂无已上传材料</Box>
                  )}
                </Box>
              </>
            )}
            {tab === 'program' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>专业与院校</Typography> */}
                  {status === 'Modify' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditClick('program')}
                    >
                      修改
                    </Button>
                  )}
                </Box>
                {Array.isArray(appData.programs) && appData.programs.length > 0 ? (
                  appData.programs.map((program, idx) => {
                    const isSelected = program.id === appData.selectedProgram;
                    return (
                      <Box
                        key={program.id || idx}
                        sx={{
                          mb: 2,
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: cardRadius || 2,
                          bgcolor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          boxShadow: 0,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isSelected ? 'primary.main' : 'text.primary', mb: 1, fontSize: { xs: 16, sm: 18 } }}>
                          {program.name} {program.nameEn && <span style={{ color: '#888', fontWeight: 400, fontSize: { xs: 12, sm: 14 } }}>({program.nameEn})</span>}
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                          院校：<Box component="span" sx={{ fontWeight: 600 }}>{program.university?.name}</Box>
                          {program.university?.nameEn && (
                            <Box component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: { xs: 12, sm: 14 } }}>({program.university.nameEn})</Box>
                          )}
                        </Typography>
                        {program.level && (
                          <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>层次：{program.level}</Typography>
                        )}
                        {program.tuitionFee?.international && (
                          <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                            学费：<Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{program.tuitionFee.international}</Box>
                            {program.tuitionFee.currency && <Box component="span" sx={{ ml: 0.5 }}>{program.tuitionFee.currency}</Box>}
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>暂无专业与院校信息</Box>
                )}
              </>
            )}
            {tab === 'payment' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>支付信息</Typography> */}
                  
                </Box>
                {appData.paymentInfo ? (
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: cardRadius || 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        mb: 2,
                      }}
                    >
                      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>支付金额：</Box>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>￥{appData.selectedPackage.price}</Box>
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>支付时间：</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>{appData.paymentInfo.paid_at || '—'}</Box>
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>支付渠道：</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            {appData.paymentInfo.trade_channel === 'wechat' ? '微信支付' : 
                             appData.paymentInfo.trade_channel === 'alipay' ? '支付宝' : 
                             appData.paymentInfo.trade_channel || '—'}
                          </Box>
                        </Typography>
                        <Typography sx={{ mb: 0.5, fontSize: { xs: 14, sm: 16 } }}>
                          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>支付状态：</Box>
                          <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                            {appData.payment ? '已支付' : '未支付'}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
                    {appData.payment ? '暂无支付信息' : '尚未支付'}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

function AccountPage() {
  const [form, setForm] = React.useState({
    password: '',
    repeatPassword: '',
    name: '',
    gender: '',
    degree: '',
    major: ''
  });
  const [saving, setSaving] = React.useState(false);
  const [accountModifySuccess, setSuccess] = React.useState(false);
  const [successMsg, setSuccessMsg] = React.useState('');
  const [repeatError, setRepeatError] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  // 密码规则校验函数
  const validatePassword = (pwd) => {
    if (!pwd) return '密码不能为空';
    if (pwd.length < 8) return '密码长度不能小于8位';
    if (!/[A-Z]/.test(pwd)) return '密码需包含大写字母';
    if (!/[a-z]/.test(pwd)) return '密码需包含小写字母';
    if (!/[0-9]/.test(pwd)) return '密码需包含数字';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'repeatPassword' || name === 'password') {
      setRepeatError(
        name === 'repeatPassword'
          ? value !== form.password
          : form.repeatPassword !== value
      );
      if (name === 'password') {
        setPasswordError(validatePassword(value));
      }
      if (name === 'repeatPassword' && form.password) {
        setPasswordError(validatePassword(form.password));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    // 密码和重复密码都为必填
    if (!form.password) {
      setPasswordError('密码不能为空');
      return;
    }
    if (form.password !== form.repeatPassword) {
      setRepeatError(true);
      return;
    }
    const pwdErr = validatePassword(form.password);
    if (pwdErr) {
      setPasswordError(pwdErr);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        password: form.password
      };
      const res = await apiClient.post('/api/auth/modify', payload);
      if (res && res.success) {
        setSuccess(true);
        setSuccessMsg(res.message || '保存成功！');
        setTimeout(() => {
          setSuccess(false);
          setSuccessMsg('');
        }, 2000);
      } else {
        setErrorMsg(res.message || '保存失败');
      }
    } catch (err) {
      let msg = '保存失败';
      if (err.response && err.response.data && err.response.data.message) {
        msg = err.response.data.message;
      } else if (err.message) {
        msg = err.message;
      }
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  // 只校验密码
  const isPasswordValid =
    form.password &&
    validatePassword(form.password) === '' &&
    form.password === form.repeatPassword;
  const canSubmit = isPasswordValid && !repeatError && !passwordError;

  return (
    <Box sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
          修改登录密码
        </Typography>
        <form onSubmit={handleSubmit} autoComplete="off">
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>新密码</Typography>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ddd', fontSize: 16 }}
              placeholder="请输入新密码"
            />
            {passwordError && (
              <Alert severity="error" sx={{ mt: 1, fontSize: 14, p: 1 }}>{passwordError}</Alert>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>重复新密码</Typography>
            <input
              type="password"
              name="repeatPassword"
              value={form.repeatPassword}
              onChange={handleChange}
              autoComplete="new-password"
              style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ddd', fontSize: 16 }}
              placeholder="请再次输入新密码"
            />
            {repeatError && (
              <Alert severity="error" sx={{ mt: 1, fontSize: 14, p: 1 }}>两次输入的新密码不一致</Alert>
            )}
          </Box>
          {errorMsg && <Alert severity="error" sx={{ mt: 1, mb: 2, textAlign: 'center' }}>{errorMsg}</Alert>}
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={saving || !canSubmit} sx={{ py: 1.2, fontSize: 16, fontWeight: 600 }}>
            {saving ? '保存中...' : '保存'}
          </Button>
          {accountModifySuccess === true && <Alert severity="success" sx={{ mt: 2, textAlign: 'center' }}>{successMsg || '保存成功！'}</Alert>}
        </form>
    </Box>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  // const location = useLocation(); // 不再用location.pathname判断内容
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [logoutLoading, setLogoutLoading] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // 新增：本地菜单选中状态，默认选中"我的申请"
  const [selectedMenu, setSelectedMenu] = React.useState('我的申请');
  // 新增：申请数据全局管理
  const [appData, setAppData] = React.useState({});
  const [appLoading, setAppLoading] = React.useState(true);
  const [appError, setAppError] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [currentEditTab, setCurrentEditTab] = React.useState('');
  const [editFormData, setEditFormData] = React.useState({});
  const [editErrors, setEditErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [canSave, setCanSave] = React.useState(false);
  // Snackbar状态
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  // 显示Snackbar提示
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 关闭Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 处理修改按钮点击
  const handleEditClick = (tabKey) => {
    setCurrentEditTab(tabKey);
    // 根据不同的Tab初始化表单数据
    if (tabKey === 'profile') {
      setEditFormData({
        personalInfo: { ...appData.personalInfo },
        englishLanguage: { ...appData.englishLanguage }
      });
    } else if (tabKey === 'work') {
      setEditFormData({ workExperience: appData.workExperience || [] });
    } else if (tabKey === 'contact') {
      setEditFormData({ emergencyContact: appData.emergencyContact || [] });
    } else if (tabKey === 'referee') {
      setEditFormData({ referee: appData.referee || [] });
    } else if (tabKey === 'additional') {
      setEditFormData({ additionalInformation: appData.additionalInformation || {} });
    } else {
      setEditFormData({ [tabKey]: appData[tabKey] });
    }
    setEditErrors({});
    setEditDialogOpen(true);
  };





  // 处理修改确认
  const handleEditConfirm = async (updatedData) => {
    setSaving(true);
    try {
      // 重新获取最新数据以确保页面显示的是最新的服务器数据
      const freshData = await getApplicationInitData();
      if (freshData) {
        setAppData(freshData);
      }
      
      setEditDialogOpen(false);
      setEditFormData({});
      setEditErrors({});
      
      // 显示成功提示
      showSnackbar('修改成功！', 'success');
      
    } catch (error) {
      showSnackbar('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };



  // 登录校验：未登录则跳转首页
  React.useEffect(() => {
    if (!localStorage.getItem('auth_token')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // 进入后台即获取申请数据
  React.useEffect(() => {
    setAppLoading(true);
    getApplicationInitData()
      .then((data) => {
        setAppData(data || {});
        setAppLoading(false);
      })
      .catch((err) => {
        setAppError('加载失败');
        setAppLoading(false);
      });
  }, []);

  // 删除申请
  const handleDeleteApplication = async () => {
    setDeleting(true);
    try {
      const res = await apiClient.post('/api/application/delete');
      if (res && res.success) {
        setDeleteDialogOpen(false);
        setAppData({});
        setAppError(null);
        setAppLoading(true);
        getApplicationInitData()
          .then((data) => {
            setAppData(data || {});
            setAppLoading(false);
            showSnackbar('删除成功！', 'success');
          })
          .catch(() => {
            setAppError('加载失败');
            setAppLoading(false);
          });
      } else {
        showSnackbar(res.message || '删除失败', 'error');
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err.message || '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await apiClient.post('/api/logout');
    } catch (e) {
      // 忽略错误，依然清理本地
    } finally {
      localStorage.removeItem('auth_token');
      setLogoutLoading(false);
      handleMenuClose();
      navigate('/login');
    }
  };
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // 侧边栏内容
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: theme.palette.background.paper, borderRight: `1px solid ${theme.palette.divider}` }}>
      <Toolbar sx={{ minHeight: 64 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1, color: theme.palette.primary.main }}>
            管理后台
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, mt: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            // component="a"
            // href={item.path}
            selected={selectedMenu === item.text}
            onClick={() => {
              setSelectedMenu(item.text);
              if (isMobile) setMobileOpen(false);
            }}
            sx={{
              borderRadius: 2,
              mx: 2,
              mb: 1,
              bgcolor: selectedMenu === item.text ? theme.palette.primary.light : 'transparent',
              color: selectedMenu === item.text ? theme.palette.primary.main : theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              transition: 'all 0.2s',
            }}
          >
            <ListItemIcon sx={{ color: selectedMenu === item.text ? theme.palette.primary.main : theme.palette.text.secondary }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, textAlign: 'center', color: theme.palette.grey[500], fontSize: 13 }}>
        © 2025 StudyMalaysia
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: theme.palette.grey[100], minHeight: '100vh' }}>
      <CssBaseline />
      {/* 顶部导航栏 */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 2px 8px 0 rgba(25, 118, 210, 0.04)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: 64, justifyContent: 'space-between' }}>
          {isMobile && (
            <IconButton color="primary" edge="start" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: 1 }}>
            
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              startIcon={<HomeIcon />}
              color="primary"
              variant="text"
              sx={{ fontWeight: 500, mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
              onClick={() => navigate('/')}
            >
              返回首页
            </Button>
            <Tooltip title="用户菜单">
              <IconButton onClick={handleMenuOpen} size="large" sx={{ ml: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>A</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleLogout} disabled={logoutLoading}>
                {logoutLoading ? '正在退出...' : '退出登录'}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {/* 侧边栏 */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="sidebar"
      >
        {/* 移动端抽屉 */}
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
              bgcolor: theme.palette.background.paper,
              pt: 0,
              overflowX: 'hidden', // 关键：隐藏横向滚动条
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      {/* 主内容区 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: theme.palette.grey[100],
          p: { xs: 0.5, md: 1 }, // 再缩小一级
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ minHeight: 64 }} />
        <Box
          sx={{
            p: { xs: 1, md: 1.5 }, // 再缩小一级
            mt: 1, // 再缩小一级
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: 320,
          }}
        >
          {selectedMenu === '我的申请' && (
            <Box sx={{ width: '100%' }}>
              {/* 申请流程stepper - 原首页内容 */}
              <Box sx={{ width: '100%', bgcolor: theme.palette.background.paper, borderRadius: 1, boxShadow: 1, p: { xs: 1, md: 1.5 }, mb: 2, position: 'relative' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <span>申请进度</span>
                  {/* 只在Submitted状态显示删除按钮 */}
                  {appData.stage === 'Submitted' && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
                    >
                      删除申请
                    </Button>
                  )}
                </Typography>
                {appData.stage ? (
                  <>
                    <Stepper 
                      alternativeLabel 
                      activeStep={progressData.findIndex(item => item.en === appData.stage)} 
                      orientation="horizontal"
                      sx={{
                        '& .MuiStepLabel-root': {
                          '& .MuiStepLabel-label': {
                            fontSize: { xs: 12, sm: 14 },
                            lineHeight: 1.2,
                          }
                        },
                        '& .MuiStepIcon-root': {
                          fontSize: { xs: 20, sm: 24 },
                        }
                      }}
                    >
                      {progressData.map((activity, idx) => {
                        let color = 'grey';
                        let isRejected = false;
                        let completed = false;
                        const currentIdx = progressData.findIndex(item => item.en === appData.stage);
                        const status = appData.status;
                        if (idx < currentIdx) {
                          color = 'green';
                          completed = true;
                        } else if (idx === currentIdx) {
                          if (status === 'In Progress') {
                            color = 'orange';
                          } else if (status === 'Reject') {
                            color = 'red';
                            isRejected = true;
                          } else if (status === 'Modify') {
                            color = 'orange';
                          }
                        }
                        return (
                          <Step key={activity.en} completed={completed}>
                            <StepLabel
                              StepIconComponent={isRejected ? (() => <CloseIcon color="error" />) : undefined}
                              sx={{ 
                                color: color + ' !important', 
                                '& .MuiStepIcon-root': { color: color + ' !important' },
                                '& .MuiStepLabel-label': {
                                  fontSize: { xs: 11, sm: 12, md: 14 },
                                  wordBreak: 'break-word',
                                }
                              }}
                            >
                              {activity.cn}
                            </StepLabel>
                          </Step>
                        );
                      })}
                    </Stepper>
                    {appData.reason && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {appData.reason}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 8, fontSize: 18 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => navigate('/services')}
                      sx={{ fontSize: 18, px: 5, py: 2 }}
                    >
                      开始申请
                    </Button>
                  </Box>
                )}
              </Box>
              
              {/* 申请详细信息 - 原我的申请内容 */}
              <ApplicationPage 
                appData={appData} 
                loading={appLoading} 
                error={appError} 
                contentPadding={{ xs: 1, md: 1.5 }} 
                cardRadius={1}
                onEditClick={handleEditClick}
                status={appData.status}
              />
            </Box>
          )}
          {selectedMenu === '我的账户' && (
            <Box sx={{ width: { xs: '100%', md: '50%' }, bgcolor: theme.palette.background.paper, borderRadius: 1, boxShadow: 1, p: { xs: 1, md: 1.5 } }}>
              <AccountPage />
            </Box>
          )}
        </Box>
        {/* 删除确认弹窗 */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>确认删除申请</DialogTitle>
          <DialogContent>
            <Typography>确定要删除当前申请吗？此操作不可恢复。</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>取消</Button>
            <Button onClick={handleDeleteApplication} color="error" variant="contained" disabled={deleting}>
              {deleting ? '删除中...' : '确认删除'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* 修改确认弹窗 */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              minHeight: '60vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              修改{applicationTabs.find(tab => tab.key === currentEditTab)?.label || '申请'}信息
            </Typography>
            <IconButton
              onClick={() => setEditDialogOpen(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
            {/* 表单内容区域 - 根据currentEditTab显示不同的表单 */}
            <Box sx={{ minHeight: '40vh' }}>
              {currentEditTab === 'profile' && (
                <PersonalInfoForm
                  initialData={{
                    personalInfo: editFormData.personalInfo || {},
                    englishLanguage: editFormData.englishLanguage || {}
                  }}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData(data);
                      handleEditConfirm(data);
                    } else {
                      // 保存失败
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'education' && (
                <EducationForm
                  initialData={editFormData.education || []}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData({ education: data });
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'work' && (
                <WorkExperienceForm
                  initialData={editFormData.workExperience || []}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData({ workExperience: data });
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'contact' && (
                <EmergencyContactForm
                  initialData={editFormData.emergencyContact || []}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData({ emergencyContact: data });
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'referee' && (
                <RefereeForm
                  initialData={editFormData.referee || []}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData({ referee: data });
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'additional' && (
                <AdditionalInfoForm
                  initialData={editFormData.additionalInformation || {}}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      setEditFormData({ additionalInformation: data });
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                />
              )}
              
              {currentEditTab === 'sevice' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                    服务套餐编辑表单
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    这里将放置服务套餐选择表单，包括套餐类型、价格、服务内容等
                  </Typography>
                  {/* TODO: 服务套餐表单组件将在这里 */}
                  <Box sx={{ 
                    p: 3, 
                    border: '2px dashed #e0e0e0', 
                    borderRadius: 2, 
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography color="text.secondary">
                      服务套餐编辑表单将在这里显示
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {currentEditTab === 'materials' && (
                <MaterialsForm
                  selectedProgram={appData?.selectedProgram}
                  selectedLevel={appData?.selectedLevel}
                  onSave={(data) => {
                    if (data) {
                      // 保存成功
                      handleEditConfirm();
                    } else {
                      // 保存失败（验证失败或API错误）
                      setSaving(false);
                    }
                  }}
                  onCancel={() => setEditDialogOpen(false)}
                  loading={saving}
                  onSaveStateChange={setCanSave}
                />
              )}
              
              {currentEditTab === 'program' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                    专业与院校编辑表单
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    这里将放置专业与院校选择表单，包括院校选择、专业选择、申请层次等
                  </Typography>
                  {/* TODO: 专业与院校表单组件将在这里 */}
                  <Box sx={{ 
                    p: 3, 
                    border: '2px dashed #e0e0e0', 
                    borderRadius: 2, 
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography color="text.secondary">
                      专业与院校编辑表单将在这里显示
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {currentEditTab === 'payment' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                    支付信息编辑表单
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    这里将放置支付信息编辑表单，包括支付方式、支付状态等
                  </Typography>
                  {/* TODO: 支付信息表单组件将在这里 */}
                  <Box sx={{ 
                    p: 3, 
                    border: '2px dashed #e0e0e0', 
                    borderRadius: 2, 
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography color="text.secondary">
                      支付信息编辑表单将在这里显示
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            borderTop: '1px solid #e0e0e0',
            gap: 2
          }}>
            <Button 
              onClick={() => setEditDialogOpen(false)} 
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              取消
            </Button>
            <Button 
              onClick={() => {
                // 触发表单保存逻辑
                setSaving(true);
              }} 
              color="primary" 
              variant="contained"
              disabled={saving || (currentEditTab === 'materials' && !canSave)}
              sx={{ minWidth: 100 }}
            >
              {saving ? '保存中...' : '保存修改'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar提示 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
} 